import firebase from './firebase';

function callables() {
  return firebase.app().functions('us-central1');
}

function call(name, data) {
  return callables().httpsCallable(name)(data || {}).then((result) => result.data);
}

export function callableErrorMessage(error) {
  if (!error) {
    return 'Something went wrong.';
  }
  if (error.code === 'permission-denied') {
    return 'Not an admin.';
  }
  if (error.code === 'unauthenticated') {
    return 'Sign in required.';
  }
  return error.message || 'Something went wrong.';
}

export function adminCheck() {
  return call('adminCheck');
}

export function adminListKonjoMatches() {
  return call('adminListKonjoMatches');
}

export function adminCountKonjoMatches() {
  return call('adminListKonjoMatches', { count_only: true });
}

export function adminSearchKonjoMatches(query) {
  return call('adminSearchKonjoMatches', { query });
}

export function adminMarkKonjoMatchPaid(matchId) {
  return call('adminMarkKonjoMatchPaid', { match_id: matchId });
}

export function adminDeleteKonjoMatch(matchId) {
  return call('adminDeleteKonjoMatch', { match_id: matchId });
}

export function adminListExpiredKonjoMatches() {
  return call('adminListExpiredKonjoMatches');
}

export function adminRevealKonjoMatchNumber(matchId, userId, source) {
  return call('adminRevealKonjoMatchNumber', {
    match_id: matchId,
    user_id: userId,
    source: source || 'live'
  });
}

export function reviewNewUsers() {
  return call('ReviewNewUsers');
}

export function reviewOldUsers() {
  return call('ReviewOldUsers');
}

export function reviewReports() {
  return call('ReviewReports');
}

export function reviewFeedback() {
  return call('ReviewFeedback');
}

export function adminGetUsers(userIds) {
  return call('adminGetUsers', { user_ids: userIds });
}

export function adminGetAddresses(userIds) {
  return call('adminGetAddresses', { user_ids: userIds });
}

export function adminGetReports() {
  return call('adminGetReports');
}

export function adminGetFeedback() {
  return call('adminGetFeedback');
}

export function adminMarkFeedback(items) {
  return call('adminMarkFeedback', { items: items || [] });
}

export function mergeUserAddresses(users, addresses) {
  const map = addresses || {};
  const apply = (user) => {
    if (!user || !Object.prototype.hasOwnProperty.call(map, user.userId)) {
      return user;
    }
    return Object.assign({}, user, {
      address: map[user.userId] || '',
      addressResolved: true
    });
  };

  if (Array.isArray(users)) {
    return users.map(apply);
  }

  const next = {};
  Object.keys(users || {}).forEach((id) => {
    next[id] = apply(users[id]);
  });
  return next;
}

export function fillUserAddresses(users, setUsers, onUsersUpdated) {
  const list = Array.isArray(users) ? users : Object.keys(users || {}).map((id) => users[id]);
  const ids = list.map((user) => user && user.userId).filter(Boolean);
  if (ids.length === 0) {
    return Promise.resolve();
  }

  const apply = (addresses) => {
    setUsers((current) => {
      const next = mergeUserAddresses(current, addresses);
      if (onUsersUpdated) {
        Promise.resolve().then(() => onUsersUpdated(next));
      }
      return next;
    });
  };

  return adminGetAddresses(ids)
    .then((addresses) => {
      apply(addresses);
    })
    .catch((error) => {
      console.warn('adminGetAddresses failed', error && error.code, error && error.message);
      const empty = {};
      ids.forEach((id) => {
        empty[id] = '';
      });
      apply(empty);
    });
}

export function adminReviewAction(action, userIds, reportIds) {
  return call('adminReviewAction', {
    action,
    user_ids: userIds || [],
    report_ids: reportIds || []
  });
}

export function adminUpdateLocation(userIds) {
  return call('adminUpdateLocation', { user_ids: userIds || [] });
}

export function adminUpdateGender(userIds) {
  return call('adminUpdateGender', { user_ids: userIds || [] });
}

export function accountFetchRequest(search, quick) {
  return call('accountFetchRequest', { search, quick });
}

export function adminGetBannedEmails() {
  return call('adminGetBannedEmails');
}

export function replyEmail(messageIds, reply) {
  return call('replyEmail', { messageIds: messageIds || [], reply });
}

export function adminMarkEmailsRead(messageIds) {
  return call('adminMarkEmailsRead', { messageIds: messageIds || [] });
}

export const ENABLE_EMAIL_REPLIES = [
  {
    id: 'photo-policy',
    title: 'Photo Policy',
    body: 'Hello,\n\nWe regret to inform you that the photos you have uploaded do not meet our verification policy standards. As a result, we would like to request that you upload a selfie image and additional photos that are consistent with the verification requirements. We have enabled access to your account, which will enable you to regain full functionality.\n\nThank you for your understanding and cooperation.\n\nSincerely,\nThe Konjo Crew\nhttps://konjoapp.com/download'
  },
  {
    id: 'no-explanation',
    title: 'No explanation needed',
    body: 'Hello,\n\nWe have unlocked your account, allowing you to regain recreate an account.\n\nThanks,\nKonjo Crew\nhttps://konjoapp.com/download'
  }
];

export const DISABLE_EMAIL_REPLIES = [
  {
    id: 'fake-photos',
    title: 'Fake Photos',
    body: 'We regret to inform you that your account has been suspended again from our app. Our team has found that you have uploaded pictures that are not of you or visibly showing you, which violates our community guidelines.\n\nAs you are aware, we take the safety and security of our users very seriously, and uploading fake pictures is not acceptable on our platform. This is the second time your account has been suspended for this reason, and we have no choice but to ban you permanently from our app.\n\nWe understand that this may come as a disappointment to you, but we must uphold our policies to ensure a safe and authentic community for all users. We urge you to review our community guidelines before creating a new account on our platform.\n\nThank you for your understanding.\n\nBest regards,\nKonjo Crew'
  },
  {
    id: 'guidelines',
    title: 'Violation of Guidelines',
    body: 'Hello,\n\nWe regret to inform you that your account has been permanently suspended due to violations of our community guidelines. Our team has reviewed your activity and determined that it does not align with the respectful and safe environment we strive to maintain for all users.\n\nAt Konjo App, we are committed to providing a space where everyone can connect, meet new people, and build relationships in a respectful and safe manner. Behavior that harasses, abuses, or deceives others goes against the core values of our platform and will not be tolerated.\n\nAs a result of these actions, your account has been banned from our service. Please be aware that our decision is final, and you will not be able to access our platform or create a new account.\n\nWe encourage all users to review our terms of service and community guidelines to ensure that everyone can continue to enjoy a positive and safe experience on Konjo App.\n\nThank you for your understanding.\n\nBest regards,\nKonjo Crew'
  },
  {
    id: 'underage',
    title: 'Underage',
    body: 'We regret to inform you that your account has been permanently suspended as it was found that you are under the minimum age required to use Konjo App. As stated in our terms of service, users must be at least 18 years old to create an account and participate in our platform.\n\nWe take the safety and security of our users seriously and must enforce these guidelines to ensure a positive and safe environment. Please review our age requirements before attempting to create an account in the future.\n\nThank you for your understanding.\n\nBest regards,\nKonjo Crew'
  }
];

export function enableUser(userId) {
  return call('enableUser', { user_id: userId });
}

export function deleteAccount(userId) {
  return call('deleteAccount', { user_id: userId });
}

export function signInWithGoogle() {
  const provider = new firebase.auth.GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return firebase.auth().signInWithPopup(provider);
}

export function signOutAdmin() {
  return firebase.auth().signOut();
}

export function copyText(value) {
  if (!value) {
    return Promise.resolve();
  }
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(value);
  }
  return Promise.resolve();
}

export function confirmAction(message) {
  return window.confirm(message);
}

export function userLocationLines(user) {
  if (!user) {
    return [];
  }

  if (user.address) {
    return [user.address];
  }

  if (!user.addressResolved) {
    return [];
  }

  const location = user.location || {};
  const hasCoords = location.latitude != null || location.longitude != null;
  return hasCoords ? ['No address'] : [];
}

export function userPhotoList(user) {
  if (!user) {
    return [];
  }
  const photos = [];
  if (user.verifyImage && user.verifyImage.url) {
    photos.push(user.verifyImage);
  }
  (user.photos || []).forEach((photo) => {
    if (photo && photo.url) {
      photos.push(photo);
    }
  });
  return photos;
}

export function userPhotoCountLabel(user) {
  const count = userPhotoList(user).length;
  return count === 1 ? '1 photo' : `${count} photos`;
}

export function userBioLines(user) {
  if (!user) {
    return [];
  }

  const lines = [];
  const pushLine = (label, value) => {
    if (value) {
      lines.push({ label, value });
    }
  };

  pushLine('Bio', user.bio);
  pushLine('Interests', user.interests);
  if (user.prompt && user.prompt.question && user.prompt.answer) {
    pushLine(user.prompt.question, user.prompt.answer);
  }
  pushLine('Work', user.work);
  pushLine('Job title', user.jobTitle);
  pushLine('School', user.school);
  pushLine('Education', user.education);
  pushLine('Religion', user.religion);
  pushLine('Children', user.children);
  pushLine('Family', user.family);
  pushLine('Hometown', user.hometown);
  pushLine('Politics', user.politics);
  pushLine('Drinking', user.drinking);
  pushLine('Smoking', user.smoking);
  pushLine('Drugs', user.drugs);
  return lines;
}
